// File: src/components/CalendarView.js
// Public race calendar, as a month grid or a flat list.
//
// Lists organizer-created events alongside the legacy series races that predate
// them. Legacy rows are historical records: they carry no venue or organizer and
// cannot be entered, so they are marked as such rather than pretending to be
// full events.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, CalendarDays, MapPin, Users, Trophy, ChevronRight, ChevronLeft, LayoutGrid, List } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { formatDateForDisplay, getCurrentYear, toLocalISODate } from '../utils/utils';
import { isChampionship, championshipBadgeClass, useChampionshipLabel } from '../utils/championships';
import { apiGet } from '../services/api';
import { getCountry } from '../data/countries';
import { useCountryName } from '../utils/countryNames';

// Literal class maps - build-time Tailwind cannot see constructed names.
const STATUS_CLASSES = {
  published: 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400',
  cancelled: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400'
};

// Chip colours inside a day cell, by where the race's entry stands: yellow
// before it opens, green while it is open, grey once it has closed. That is the
// question a visitor scanning the calendar is actually asking.
//
// A championship is no longer coloured differently. It carries a trophy on the
// chip and a badge in the list instead, so its registration state stays as
// readable as everything else's.
//
// The greys are literal slates rather than the paper-alt token the rest of the
// app leans on. paper-alt is one step from the page it sits on - 1.07:1 against
// --paper in dark mode - which is right for a panel and wrong for a chip: closed
// races stopped reading as chips at all beside yellow at 11:1 and green at 5:1.
// Slate stays deliberately quieter than those two, because "closed" should
// recede. It just has to still be there.
const CHIP_CLASSES = {
  notYetOpen: 'bg-yellow-400 text-yellow-950 hover:opacity-90',
  open: 'bg-green-600 text-white hover:opacity-90',
  closed: 'bg-slate-300 text-slate-700 dark:bg-slate-600 dark:text-slate-100 hover:opacity-90',
  cancelled: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 line-through',
  // A legacy series race has no entry window at all - it is a historical record,
  // and there are a great many of them, so it keeps the quieter outlined style.
  race: 'bg-paper-alt text-ink-muted border border-line'
};

// The same three states as words, for the list view where colour alone would
// not be enough.
const ENTRY_STATE_BADGES = {
  notYetOpen: 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-800 dark:text-yellow-300',
  open: 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400',
  closed: 'bg-slate-200 text-slate-700 dark:bg-slate-600 dark:text-slate-100'
};

const ENTRY_STATE_LABELS = {
  notYetOpen: 'calendarEntryNotYetOpen',
  open: 'calendarEntryOpen',
  closed: 'calendarEntryClosed'
};

// How many chips fit in a day cell before collapsing into "+N more".
const MAX_CHIPS_PER_DAY = 3;

function CalendarView({ initialCountry }) {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState(() => ({
    year: getCurrentYear(),
    month: new Date().getMonth()
  }));
  const { year, month } = period;
  // Follows the country picked in the page header. It used to be a country
  // select of its own, which was fine while the header's country lived behind a
  // hamburger on another page - now the two would sit inches apart and disagree.
  const [country, setCountry] = useState(initialCountry || '');
  useEffect(() => { setCountry(initialCountry || ''); }, [initialCountry]);
  // The one thing the header's country picker cannot express: no filter at all.
  const [allCountries, setAllCountries] = useState(false);
  const [view, setView] = useState('grid');

  // The whole year is fetched at once, so paging between months is instant and
  // does not hit the API again.
  const loadCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ year: String(year) });
      if (country && !allCountries) params.set('country', country);
      setItems(await apiGet(`/public/calendar?${params.toString()}`));
      setError('');
    } catch (err) {
      setError(err.message || t('calendarFailedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [year, country, allCountries, t]);

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  const currentYear = getCurrentYear();
  const years = [];
  for (let y = currentYear + 2; y >= currentYear - 5; y--) years.push(y);

  const monthNames = t('calendarMonthsFull').split(',');
  const shortMonthNames = t('calendarMonths').split(',');
  const weekdays = t('calendarWeekdays').split(',');
  const today = toLocalISODate(new Date());

  // Stepping past either end of the year rolls over, which refetches. The
  // functional update keeps rapid clicks from collapsing into one step.
  const stepMonth = (delta) => {
    setPeriod(prev => {
      const absolute = prev.year * 12 + prev.month + delta;
      return { year: Math.floor(absolute / 12), month: ((absolute % 12) + 12) % 12 };
    });
  };

  // The way back from wherever the stepper - or the year select above it - has
  // wandered to. Offered only when it would actually do something: on the
  // current month the button is just a control that says "stay here".
  const thisMonth = { year: currentYear, month: new Date().getMonth() };
  const showingThisMonth = year === thisMonth.year && month === thisMonth.month;

  // Six Monday-first weeks covering the month, each day carrying the items that
  // fall on it. A multi-day event appears on every day it spans.
  const weeks = useMemo(() => {
    const firstOfMonth = new Date(year, month, 1);
    // getDay() is 0=Sunday; shift so Monday is the first column.
    const leading = (firstOfMonth.getDay() + 6) % 7;

    const cursor = new Date(year, month, 1 - leading);
    const result = [];

    for (let week = 0; week < 6; week++) {
      const days = [];
      for (let day = 0; day < 7; day++) {
        const date = toLocalISODate(cursor);
        days.push({
          date,
          dayOfMonth: cursor.getDate(),
          inMonth: cursor.getMonth() === month,
          isToday: date === today,
          // Date-only strings compare correctly as strings, which sidesteps
          // timezone conversion entirely.
          items: items.filter(item => date >= item.start_date && date <= (item.end_date || item.start_date))
        });
        cursor.setDate(cursor.getDate() + 1);
      }
      result.push(days);
    }

    // Drop a trailing all-outside week so short months don't render an empty row.
    if (result[5].every(d => !d.inMonth)) result.pop();
    return result;
  }, [year, month, items, today]);

  const monthHasItems = weeks.some(week => week.some(day => day.items.length > 0));

  // The list view keeps its month grouping across the whole year.
  const groupedByMonth = items.reduce((acc, item) => {
    const index = parseInt(item.start_date.slice(5, 7), 10) - 1;
    (acc[index] = acc[index] || []).push(item);
    return acc;
  }, {});

  return (
    <div>
      <div className="flex flex-wrap items-end gap-4 mb-4">
        <div className="w-40">
          <label className="block text-ink-muted mb-2 font-semibold">{t('selectYear')}:</label>
          <select
            value={year}
            onChange={(e) => setPeriod(prev => ({ ...prev, year: parseInt(e.target.value, 10) }))}
            className="w-full px-4 py-2 bg-paper border border-line rounded text-ink"
          >
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        <label className="flex items-center gap-2 cursor-pointer text-ink py-2">
          <input
            type="checkbox"
            checked={allCountries}
            onChange={(e) => setAllCountries(e.target.checked)}
            className="w-4 h-4"
          />
          {t('calendarAllCountries')}
        </label>
      </div>

      {/* Month stepper and view toggle. The toggle only governs wide screens -
          a seven-column grid is unusable on a phone, so narrow viewports always
          get the list. */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className={`items-center gap-2 ${view === 'grid' ? 'hidden md:flex' : 'hidden'}`}>
          <button
            onClick={() => stepMonth(-1)}
            className="bg-paper-alt hover:opacity-80 text-ink p-2 rounded"
            title={t('calendarPrevMonth')}
            aria-label={t('calendarPrevMonth')}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-ink font-semibold min-w-[10rem] text-center">
            {monthNames[month]} {year}
          </span>
          <button
            onClick={() => stepMonth(1)}
            className="bg-paper-alt hover:opacity-80 text-ink p-2 rounded"
            title={t('calendarNextMonth')}
            aria-label={t('calendarNextMonth')}
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {!showingThisMonth && (
            <button
              onClick={() => setPeriod(thisMonth)}
              className="bg-paper-alt hover:opacity-80 text-ink px-3 py-2 rounded font-semibold flex items-center gap-2"
              title={t('calendarToday')}
            >
              <CalendarDays className="w-4 h-4" />
              {t('calendarToday')}
            </button>
          )}
        </div>

        <div className="hidden md:flex items-center gap-1 bg-paper-alt rounded p-1 ml-auto">
          <button
            onClick={() => setView('grid')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-semibold ${
              view === 'grid' ? 'bg-accent text-accent-ink' : 'text-ink-muted hover:text-ink'
            }`}
          >
            <LayoutGrid className="w-4 h-4" />
            {t('calendarViewGrid')}
          </button>
          <button
            onClick={() => setView('list')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-semibold ${
              view === 'list' ? 'bg-accent text-accent-ink' : 'text-ink-muted hover:text-ink'
            }`}
          >
            <List className="w-4 h-4" />
            {t('calendarViewList')}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-500/20 border border-red-500 text-red-800 dark:text-red-200 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-ink-muted">{t('calendarLoading')}</p>
      ) : (
        <>
          {/* Month grid: wide screens only, and only when chosen. */}
          {view === 'grid' && (
            <div className="hidden md:block">
              <MonthGrid
                weeks={weeks}
                weekdays={weekdays}
                monthHasItems={monthHasItems}
                t={t}
              />
            </div>
          )}

          {/* The list: always on narrow screens, and on wide ones when chosen. */}
          <div className={view === 'grid' ? 'md:hidden' : ''}>
            <EventList grouped={groupedByMonth} months={shortMonthNames} year={year} t={t} />
          </div>
        </>
      )}
    </div>
  );
}

function MonthGrid({ weeks, weekdays, monthHasItems, t }) {
  return (
    <div className="bg-paper border border-line rounded-lg overflow-hidden">
      <div className="grid grid-cols-7">
        {weekdays.map(day => (
          <div
            key={day}
            className="px-2 py-2 text-center text-ink-muted text-xs font-semibold uppercase tracking-wide border-b border-line"
          >
            {day}
          </div>
        ))}

        {weeks.flat().map(day => (
          <div
            key={day.date}
            className={`min-h-[7rem] p-1.5 border-b border-r border-line last:border-r-0 ${
              day.inMonth ? '' : 'bg-paper-alt/40'
            }`}
          >
            <div
              className={`text-xs mb-1 font-mono-num flex items-center justify-center w-6 h-6 rounded-full ${
                day.isToday
                  ? 'bg-accent text-accent-ink font-bold'
                  : day.inMonth ? 'text-ink' : 'text-ink-muted opacity-50'
              }`}
              title={day.isToday ? t('calendarToday') : undefined}
            >
              {day.dayOfMonth}
            </div>

            <div className="space-y-1">
              {day.items.slice(0, MAX_CHIPS_PER_DAY).map(item => (
                <DayChip key={`${item.source}-${item.id}`} item={item} />
              ))}
              {day.items.length > MAX_CHIPS_PER_DAY && (
                <div className="text-[0.65rem] text-ink-muted px-1">
                  {t('calendarMoreEvents').replace('{count}', day.items.length - MAX_CHIPS_PER_DAY)}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {!monthHasItems && (
        <p className="text-ink-muted text-center py-4 border-t border-line">
          {t('calendarNoEventsThisMonth')}
        </p>
      )}
    </div>
  );
}

function DayChip({ item }) {
  const isEvent = item.source === 'event';
  // A cancelled race is not "closed for entry", it is off - so it keeps its own
  // colour ahead of the entry states. An event served by a build that predates
  // entry_state falls back to closed, the honest reading of "we do not know".
  const tone = !isEvent
    ? 'race'
    : item.status === 'cancelled'
      ? 'cancelled'
      : CHIP_CLASSES[item.entry_state] ? item.entry_state : 'closed';

  // A legacy race is identified by its series, not its own name: in real data
  // the name is often just the venue, so two series racing at the same place on
  // the same day would otherwise render as two identical chips.
  const seriesName = !isEvent ? (item.classes[0] && item.classes[0].name) : null;
  const heading = seriesName || item.name;
  const subheading = seriesName && item.name !== seriesName ? item.name : null;

  const chip = (
    <div
      className={`text-[0.7rem] leading-tight px-1.5 py-1 rounded ${CHIP_CLASSES[tone]}`}
      title={subheading ? `${heading} — ${subheading}` : heading}
    >
      <div className="truncate flex items-center gap-1">
        {isChampionship(item.championship) && <Trophy className="w-3 h-3 flex-shrink-0" />}
        <span className="truncate">{heading}</span>
      </div>
      {subheading && <div className="truncate opacity-70">{subheading}</div>}
    </div>
  );

  // Only real events have somewhere to go.
  return isEvent ? <Link to={`/event/${item.id}`} className="block">{chip}</Link> : chip;
}

function EventList({ grouped, months, year, t }) {
  const monthIndexes = Object.keys(grouped).map(Number).sort((a, b) => a - b);

  if (monthIndexes.length === 0) {
    return (
      <div className="bg-paper rounded-lg p-12 text-center border border-line">
        <Calendar className="w-16 h-16 text-ink-muted mx-auto mb-4" />
        <p className="text-ink-muted text-lg">{t('calendarNoEvents')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {monthIndexes.map(month => (
        <div key={month}>
          <h2 className="text-ink-muted font-semibold uppercase tracking-wide text-sm mb-3">
            {months[month]} {year}
          </h2>
          <div className="space-y-3">
            {grouped[month].map(item => (
              <CalendarRow key={`${item.source}-${item.id}`} item={item} t={t} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function CalendarRow({ item, t }) {
  const countryName = useCountryName();
  const isEvent = item.source === 'event';
  const location = [item.venue, item.city].filter(Boolean).join(', ');
  const championshipLabel = useChampionshipLabel();
  // A championship is on this calendar whoever is hosting it, so where that is
  // has to be on the row. Without it a Finnish reader sees a race they cannot
  // place and assumes the calendar is broken.
  const host = isChampionship(item.championship)
    ? getCountry(item.country)
    : null;

  const body = (
    <div className="bg-paper border border-line rounded-lg p-4 flex items-start justify-between gap-4 hover:border-accent transition-colors">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-ink font-semibold text-lg">{item.name}</span>

          {item.status === 'cancelled' && (
            <span className={`text-xs font-semibold px-2 py-1 rounded ${STATUS_CLASSES.cancelled}`}>
              {t('eventStatusCancelled')}
            </span>
          )}

          {/* The chip colour from the grid, said in words for the list. */}
          {isEvent && item.status !== 'cancelled' && ENTRY_STATE_BADGES[item.entry_state] && (
            <span className={`text-xs font-semibold px-2 py-1 rounded ${ENTRY_STATE_BADGES[item.entry_state]}`}>
              {t(ENTRY_STATE_LABELS[item.entry_state])}
            </span>
          )}

          {!isEvent && (
            <span className="text-xs font-semibold px-2 py-1 rounded bg-paper-alt text-ink-muted">
              {t('calendarLegacyRace')}
            </span>
          )}

          {isChampionship(item.championship) && (
            <span className={`text-xs font-semibold px-2 py-1 rounded flex items-center gap-1 ${championshipBadgeClass(item.championship)}`}>
              <Trophy className="w-3 h-3" />
              {championshipLabel(item.championship)}
            </span>
          )}
        </div>

        <div className="text-ink-muted text-sm mt-1 flex items-center gap-2 flex-wrap">
          <Calendar className="w-4 h-4" />
          {formatDateForDisplay(item.start_date)}
          {item.end_date && ` – ${formatDateForDisplay(item.end_date)}`}

          {host && (
            <>
              <MapPin className="w-4 h-4 ml-2" />
              <span>{host.flag} {countryName(host)}</span>
            </>
          )}

          {location && (
            <>
              {!host && <MapPin className="w-4 h-4 ml-2" />}
              {host ? `· ${location}` : location}
            </>
          )}

          {isEvent && item.entry_count > 0 && (
            <>
              <Users className="w-4 h-4 ml-2" />
              {item.entry_count} {t('calendarEntrants')}
            </>
          )}
        </div>

        {item.organizer && (
          <div className="text-ink-muted text-sm mt-1">{item.organizer}</div>
        )}

        {item.classes.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {item.classes.map((cls, index) => (
              <span
                key={cls.id || `${item.id}-${index}`}
                className="text-xs px-2 py-1 rounded bg-paper-alt text-ink-muted"
              >
                {cls.name}
              </span>
            ))}
          </div>
        )}
      </div>

      {isEvent && <ChevronRight className="w-5 h-5 text-ink-muted flex-shrink-0 mt-1" />}
    </div>
  );

  return isEvent ? <Link to={`/event/${item.id}`} className="block">{body}</Link> : body;
}

export default CalendarView;
